import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Table, Button, Badge, Row, Col, Form, Spinner, Modal, Alert } from "react-bootstrap";
import PageWrapperForm from "../components/PageWrapperForm";
import { extraService } from "../service/extraService";

const ExtraServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("ALL");

  // Modal State for Add / Edit
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  // Form Fields
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    category: "",
    description: "",
    materialClick: "",
    webUrl: "",
    status: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await extraService.getAll();
      setServices(res?.data || []);
    } catch (err) {
      console.warn("Error loading extra services:", err);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleOpenAdd = () => {
    setSelectedItem(null);
    setFormData({
      id: null,
      name: "",
      category: "",
      description: "",
      materialClick: "",
      webUrl: "",
      status: true,
    });
    setSelectedFile(null);
    setPreviewUrl("");
    setFormError(null);
    setFormSuccess(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      id: item.id,
      name: item.name,
      category: item.category,
      description: item.description,
      materialClick: item.materialClick,
      webUrl: item.webUrl,
      status: item.status,
    });
    setSelectedFile(null);
    setPreviewUrl(
      item.image
        ? item.image.startsWith("http")
          ? item.image
          : `https://pro.mokshiq.in/media/${item.image}`
        : ""
    );
    setFormError(null);
    setFormSuccess(null);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formData.name.trim()) {
      setFormError("Service Name is required.");
      return;
    }
    if (!formData.category.trim()) {
      setFormError("Category is required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await extraService.addOrUpdate(formData, selectedFile);
      if (res && res.status) {
        setFormSuccess(res.message || "Saved successfully!");
        setTimeout(() => {
          setShowModal(false);
          fetchServices();
        }, 1200);
      } else {
        setFormError(res?.message || "Failed to save service.");
      }
    } catch (err) {
      setFormError(err.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      const res = await extraService.remove(itemToDelete.id);
      if (res && res.status) {
        setShowDeleteModal(false);
        setItemToDelete(null);
        fetchServices();
      } else {
        alert(res?.message || "Failed to delete service.");
      }
    } catch (err) {
      alert(err.message || "Error deleting service.");
    } finally {
      setDeleting(false);
    }
  };

  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(services.map((s) => s.category).filter(Boolean)));
  }, [services]);

  const filteredServices = services.filter((s) => {
    const matchesSearch =
      (s.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.category || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      filterCategory === "ALL" || s.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <main>
      <div className="container-fluid">
        {/* Custom Header Layout */}
        <div className="row m-1 mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <div>
              <h4 className="main-title fw-bold" style={{ color: "#1a1a1a", fontSize: "1.8rem", marginBottom: "4px" }}>Extra Services Manager</h4>
              <div className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13.5px", fontWeight: "500" }}>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2", display: "flex", alignItems: "center" }}>
                  <i className="ph-duotone ph-list-dashes f-s-16"></i> <span className="ms-1">Extra Services</span>
                </a>
                <span style={{ color: "#a0a5b1" }}>/</span>
                <a href="#" className="text-decoration-none" style={{ color: "#7e57c2" }}>
                  Manager
                </a>
              </div>
            </div>

            <div>
              <button
                className="btn btn-primary d-flex align-items-center justify-content-center px-4"
                onClick={handleOpenAdd}
                style={{
                  backgroundColor: "#7e57c2", borderColor: "#7e57c2", borderRadius: "8px", height: "44px", width: "fit-content", minWidth: "160px"
                }}
              >
                <i className="iconoir-plus-circle me-2" style={{ fontSize: "20px" }}></i>
                <span className="fw-medium text-nowrap">Add</span>
              </button>
            </div>
          </div>
        </div>

        <div className="row m-1">
          <div className="col-12">
      <Card className="shadow-sm border-0">
        <Card.Body>
          {/* Top Filter Controls */}
          <Row className="mb-3 align-items-center g-2">
            <Col md={5} sm={7}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search service name, category or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>
            <Col md={4} sm={5}>
              <Form.Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="ALL">All Categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              {loading && <Spinner animation="border" size="sm" className="text-primary" />}
            </Col>
          </Row>

          {/* Data Table */}
          <div className="table-responsive">
            <Table hover striped className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Material Click</th>
                  <th>Web URL</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.length > 0 ? (
                  filteredServices.map((item, index) => {
                    const imageUrl = item.image
                      ? item.image.startsWith("http")
                        ? item.image
                        : `https://pro.mokshiq.in/media/${item.image}`
                      : null;

                    return (
                      <tr key={item.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.name}
                              style={{ width: "42px", height: "42px", objectFit: "cover", borderRadius: "8px" }}
                            />
                          ) : (
                            <div
                              className="bg-light text-muted d-flex align-items-center justify-content-center rounded"
                              style={{ width: "42px", height: "42px", fontSize: "11px" }}
                            >
                              No Img
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-bold text-dark">{item.name}</div>
                          {item.description && (
                            <small className="text-muted d-block text-truncate" style={{ maxWidth: 220 }}>
                              {item.description}
                            </small>
                          )}
                        </td>
                        <td>
                          <Badge bg="primary-subtle" text="primary" className="border border-primary-subtle">
                            {item.category}
                          </Badge>
                        </td>
                        <td>{item.materialClick || "-"}</td>
                        <td>
                          {item.webUrl ? (
                            <a
                              href={item.webUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary small text-decoration-none fw-semibold"
                            >
                              <i className="iconoir-open-new-window me-1"></i> Open Link
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <Badge bg={item.status ? "success" : "secondary"}>
                            {item.status ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-success icon-btn b-r-4"
                              type="button"
                              title="Edit Service"
                              onClick={() => handleOpenEdit(item)}
                            >
                              <i className="iconoir-edit"></i>
                            </button>
                            <button
                              className="btn btn-danger icon-btn b-r-4"
                              type="button"
                              title="Delete Service"
                              onClick={() => handleDeleteClick(item)}
                            >
                              <i className="iconoir-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      {loading ? "Loading extra services..." : "No extra services found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => !submitting && setShowModal(false)} centered backdrop="static">
        <Modal.Header closeButton={!submitting} className="bg-primary text-white py-3">
          <Modal.Title className="fs-6 text-white m-0">
            <i className="iconoir-box me-2"></i> {selectedItem ? "Edit Extra Service" : "Add New Extra Service"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="py-4">
            {formError && <Alert variant="danger" className="py-2 small">{formError}</Alert>}
            {formSuccess && <Alert variant="success" className="py-2 small">{formSuccess}</Alert>}

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Service Name *</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="Enter service name..."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Category *</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g. Credit Card, Fastag, Bill Payment..."
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Brief description of service..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Material Click</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Action key"
                    value={formData.materialClick}
                    onChange={(e) => setFormData({ ...formData, materialClick: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Web URL</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://..."
                    value={formData.webUrl}
                    onChange={(e) => setFormData({ ...formData, webUrl: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Banner Image</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
                  {previewUrl && (
                    <div className="mt-2 text-center bg-light p-2 rounded">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        style={{ maxHeight: "90px", objectFit: "contain", borderRadius: "6px" }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="extra-service-status-switch"
                  label="Active Status"
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  className="fw-semibold text-dark mt-1"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="py-2">
            <Button variant="light" size="sm" disabled={submitting} onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : selectedItem ? (
                "Update Service"
              ) : (
                "Save Service"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered size="sm">
        <Modal.Header closeButton={!deleting} className="bg-danger text-white py-2">
          <Modal.Title className="fs-6 text-white m-0">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3 text-center">
          <p className="mb-0">
            Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="py-2 justify-content-center">
          <Button variant="light" size="sm" disabled={deleting} onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" disabled={deleting} onClick={handleConfirmDelete}>
            {deleting ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ExtraServicesManagement;
